import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import type { NavigateFn } from '@hooks/useCustomNavigate.ts';
import { ROUTES } from '@router/constants.ts';
import type {
    AdminCenterClient,
    AdminCenterClientTenant,
} from '@routes/sidebar/admin-center/___shared___/api/adminCenter.api.ts';
import { setTenantActionsContext } from '@routes/sidebar/admin-center/___shared___/model/adminCenter.slice.ts';
import { CLIENT_INFO_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/constants.ts';
import { setClientEditContext } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/model/clientInfo.slice.ts';
import { TENANT_INFO_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/constants.ts';
import { setTenantEditContext } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/model/tenantInfo.slice.ts';
import {
    setIsAddNewTenantContext,
    setPublishedSteps,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import type { DataType } from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/types.ts';

export const resolveClientAndTenantFromRow = (row: DataType, rawClientsData: AdminCenterClient[], rows: DataType[]) => {
    const clientLabel = row.client ?? row.clientName ?? '';
    const client = rawClientsData.find((c: any) => c?.name === clientLabel) ?? null;

    if (!client) return { client: null, tenant: null };

    const tenantName = row.tenantName ?? '';
    let tenant = tenantName ? (getTenant(client, tenantName, row) ?? null) : null;

    if (!tenant) {
        const firstTenantRow = rows.find((r) => (r.client ?? r.clientName) === clientLabel && !!r.tenantName);
        tenant = firstTenantRow ? (getTenant(client, firstTenantRow.tenantName ?? '', firstTenantRow) ?? null) : null;
    }

    return { client, tenant };
};

export const gotoClientInfo = (navigate: NavigateFn, clientId?: string | number, tenantAlias?: string) =>
    navigate(
        `/${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.ROOT}/${clientId ?? ''}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO}/${tenantAlias ?? ''}`,
        {
            absolute: true,
        },
    );

export const gotoTenantInfo = (navigate: NavigateFn, clientId: number | string, tenantAlias?: string) =>
    navigate(
        `/${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.ROOT}/${clientId}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.TENANT_INFO}/${tenantAlias ?? ''}`,
        { absolute: true },
    );

export const getTenant = (client: AdminCenterClient, tenantName: string, row: DataType) =>
    (client.tenants ?? []).find((t: any) => t?.name === tenantName) ||
    (client.tenants ?? []).find((t: any) => t?.alias === (row as any)?.alias);

export const dispatchTenantActionsContext = (
    client: AdminCenterClient,
    tenant: AdminCenterClientTenant | undefined,
    isTenantActive: boolean,
    dispatch: AppDispatchFn,
) => {
    dispatch(
        setTenantActionsContext({
            clientId: client.id,
            clientName: client.name,
            tenantAlias: tenant?.alias ?? '',
            tenantName: tenant?.name ?? '',
            tenantType: tenant?.tenantType ?? '',
            isTenantActive,
        }),
    );
};

export const handleClientRelatedData = (client: AdminCenterClient, dispatch: AppDispatchFn) => {
    dispatch(setClientEditContext({ id: client.id, name: client.name }));
};

const handleTenantRelatedData = (tenant: AdminCenterClientTenant | undefined, dispatch: AppDispatchFn) => {
    if (tenant) {
        dispatch(
            setTenantEditContext({
                alias: tenant.alias,
            }),
        );
        dispatch(setIsAddNewTenantContext(false));
    }
};

// we can't edit tenant from table unless first 2 steps were saved
// so on edit client or tenant action we know that these steps can be marked as published
const markFirstTwoStepsAsPublished = (dispatch: AppDispatchFn) => {
    dispatch(setPublishedSteps(CLIENT_INFO_STEP_TITLE));
    dispatch(setPublishedSteps(TENANT_INFO_STEP_TITLE));
};

export const handleClientAndTenantRelatedActions = (
    client: AdminCenterClient,
    tenant: AdminCenterClientTenant | undefined,
    isTenantActive: boolean,
    dispatch: AppDispatchFn,
) => {
    handleClientRelatedData(client, dispatch);
    handleTenantRelatedData(tenant, dispatch);
    markFirstTwoStepsAsPublished(dispatch);
    dispatchTenantActionsContext(client, tenant, isTenantActive, dispatch);
};
